import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { PageShell, PageHero } from "@/components/PageShell";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

export const Route = createFileRoute("/faqs")({
  component: Faqs,
  head: () => ({
    meta: [
      { title: "FAQs, N S Physiotherapy" },
      { name: "description", content: "Answers to frequent questions about physiotherapy sessions, medical aid claims, home visits and booking." },
      { property: "og:title", content: "FAQs, N S Physiotherapy" },
      { property: "og:url", content: "/faqs" },
    ],
    links: [{ rel: "canonical", href: "/faqs" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      }),
    }],
  }),
});

type Faq = { q: string; a: string; link?: { to: string; label: string } };

const FAQS: Faq[] = [
  { q: "Do I need a referral to book a physiotherapy appointment?", a: "No referral is required. You can book directly with us online, by phone or WhatsApp." },
  { q: "Do you claim from medical aid?", a: "Yes. We submit claims at scheme rates to most major medical aids. A shortfall may apply." },
  { q: "What should I bring to my first session?", a: "Bring your ID, medical aid card (if applicable), any relevant scans or reports and comfortable clothing." },
  { q: "How long is a typical session?", a: "Initial consultations are 60 minutes; follow ups are typically 45 minutes." },
  { q: "Do you offer home visits?", a: "Yes, Physio@Home is available across Centurion, Midrand and surrounding areas. Travel fees may apply." },
  { q: "Do you offer online physiotherapy consultations?", a: "Yes. We offer online video consultations for assessment, exercise rehabilitation, ergonomic advice and follow up care, wherever you are in South Africa.", link: { to: "/services/online-physiotherapy", label: "Learn more about online physiotherapy" } },
  { q: "How do I reschedule or cancel?", a: "Use the link in your confirmation email, or contact us on 050 215 7368 at least 24 hours in advance." },
  { q: "Do you treat children?", a: "Yes, we treat patients of all ages including paediatric neurological rehabilitation." },
];

function Faqs() {
  return (
    <PageShell>
      <PageHero eyebrow="Support" title="Frequently asked questions" subtitle="Everything you need to know before your first appointment." />
      <section className="container-page py-16 max-w-3xl">
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`i-${i}`}>
              <AccordionTrigger className="text-left font-display text-primary">{f.q}</AccordionTrigger>
              <AccordionContent className="text-foreground/85">
                {f.a}
                {f.link && (
                  <Link to={f.link.to} className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-primary hover:underline">
                    {f.link.label} <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </PageShell>
  );
}
